import {
  ContentCompetency,
  ContentNugget,
  DeepDive,
  Tool,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';

type FeaturedResource = ContentNugget | Tool | PriorityPlaylist | DeepDive | ContentCompetency;

export type DashboardFeaturedItem = Readonly<{
  id: number;
  resource_type: 'content_nugget' | 'tool' | 'suggested_playbook' | 'deep_dive' | 'competency';
  resource: FeaturedResource;
  sort_order: number;
}>;

export type PaginatedFeaturedParams = Readonly<{
  range: readonly [number, number];
  shuffleSeed: number;
}>;

export type PaginatedFeaturedResult = Readonly<{
  data: readonly DashboardFeaturedItem[];
  total: number;
}>;

export const dashboardFeaturedApi = createApi({
  reducerPath: 'dashboardFeaturedApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getDashboardFeaturedResources: build.query<PaginatedFeaturedResult, PaginatedFeaturedParams>({
      queryFn: async ({ range, shuffleSeed }, _queryApi, _extraOptions, baseQuery) => {
        const params = new URLSearchParams();

        params.append('range', JSON.stringify(range));
        params.append('shuffle', String(shuffleSeed));

        const result = await baseQuery(`dashboard-featured-resource/hydrated?${params.toString()}`);

        if (result.error) {
          return { error: result.error };
        }

        const total = parseInt(
          (result.meta as { response?: Response })?.response?.headers.get('X-Total-Count') ?? '0',
          10,
        );

        return { data: { data: result.data as DashboardFeaturedItem[], total } };
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getGuestDashboardFeaturedResources: build.query<
      PaginatedFeaturedResult,
      PaginatedFeaturedParams
    >({
      queryFn: async ({ range, shuffleSeed }, _queryApi, _extraOptions, baseQuery) => {
        const params = new URLSearchParams();

        params.append('range', JSON.stringify(range));
        params.append('shuffle', String(shuffleSeed));

        const result = await baseQuery(
          `dashboard-featured-resource/hydrated/guest?${params.toString()}`,
        );

        if (result.error) {
          return { error: result.error };
        }

        const total = parseInt(
          (result.meta as { response?: Response })?.response?.headers.get('X-Total-Count') ?? '0',
          10,
        );

        return { data: { data: result.data as DashboardFeaturedItem[], total } };
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
  }),
});

export const { useGetDashboardFeaturedResourcesQuery, useGetGuestDashboardFeaturedResourcesQuery } =
  dashboardFeaturedApi;
